import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  PlusIcon,
  Trash2Icon,
} from 'lucide-react-native';
import { Host, Picker, Switch } from '@expo/ui';
import * as React from 'react';
import { Pressable, View } from 'react-native';
import { Controller, useForm, type Control } from 'react-hook-form';

import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Input } from '@sixam/ui/input';
import { Label } from '@sixam/ui/label';
import { Text } from '@sixam/ui/text';
import {
  MAX_REGISTRATION_FORM_FIELDS,
  createRegistrationFormField,
  validateRegistrationFormResponses,
  type RegistrationFormField,
  type RegistrationFormFieldType,
  type RegistrationFormResponses,
  type RegistrationFormSchema,
} from '@sixam/shared/event-registration-form-core';

const FIELD_TYPE_LABELS: Record<RegistrationFormFieldType, string> = {
  short_text: 'Short answer',
  long_text: 'Long answer',
  multiple_choice: 'Choose one',
};

function updateFields(
  schema: RegistrationFormSchema,
  updater: (fields: RegistrationFormField[]) => RegistrationFormField[]
): RegistrationFormSchema {
  return {
    ...schema,
    fields: updater([...schema.fields]),
  };
}

function removeOptionAtIndex(options: readonly string[], optionIndex: number): string[] {
  return options.filter((_, index) => index !== optionIndex);
}

function registrationFormFieldError(field: RegistrationFormField): string | null {
  if (!field.label.trim()) {
    return 'Add a question';
  }
  if (field.type !== 'multiple_choice') {
    return null;
  }
  const uniqueChoices = new Set(
    (field.options ?? []).map((option) => option.trim()).filter(Boolean)
  );
  return uniqueChoices.size < 2 ? 'Add at least 2 choices' : null;
}

export function EventRegistrationFormBuilder({
  onChange,
  schema,
}: Readonly<{
  onChange: (schema: RegistrationFormSchema) => void;
  schema: RegistrationFormSchema;
}>) {
  const [expandedFieldId, setExpandedFieldId] = React.useState<string | null>(
    schema.fields[0]?.id ?? null
  );

  function addField(type: RegistrationFormFieldType) {
    if (schema.fields.length >= MAX_REGISTRATION_FORM_FIELDS) {
      return;
    }
    const field = createRegistrationFormField(type, schema.fields);
    onChange(updateFields(schema, (fields) => [...fields, field]));
    setExpandedFieldId(field.id);
  }

  function removeField(fieldId: string) {
    onChange(updateFields(schema, (fields) => fields.filter((field) => field.id !== fieldId)));
    if (expandedFieldId === fieldId) {
      setExpandedFieldId(null);
    }
  }

  function moveField(fieldId: string, direction: -1 | 1) {
    onChange(
      updateFields(schema, (fields) => {
        const currentIndex = fields.findIndex((field) => field.id === fieldId);
        const nextIndex = currentIndex + direction;
        if (currentIndex < 0 || nextIndex < 0 || nextIndex >= fields.length) return fields;
        const next = [...fields];
        [next[currentIndex], next[nextIndex]] = [next[nextIndex], next[currentIndex]];
        return next;
      })
    );
  }

  function setFieldType(fieldId: string, type: RegistrationFormFieldType) {
    onChange(
      updateFields(schema, (fields) =>
        fields.map((field) => {
          if (field.id !== fieldId || field.type === type) {
            return field;
          }
          return {
            ...field,
            options: type === 'multiple_choice' ? ['Choice 1', 'Choice 2'] : undefined,
            type,
          };
        })
      )
    );
  }

  function patchField(fieldId: string, patch: Partial<RegistrationFormField>) {
    onChange(
      updateFields(schema, (fields) =>
        fields.map((field) => (field.id === fieldId ? { ...field, ...patch } : field))
      )
    );
  }

  function patchOption(fieldId: string, optionIndex: number, value: string) {
    onChange(
      updateFields(schema, (fields) =>
        fields.map((field) => {
          if (field.id !== fieldId || field.type !== 'multiple_choice') {
            return field;
          }
          const options = [...(field.options ?? [])];
          options[optionIndex] = value;
          return { ...field, options };
        })
      )
    );
  }

  function addOption(fieldId: string) {
    onChange(
      updateFields(schema, (fields) =>
        fields.map((field) => {
          if (field.id !== fieldId || field.type !== 'multiple_choice') {
            return field;
          }
          return {
            ...field,
            options: [...(field.options ?? []), `Choice ${(field.options?.length ?? 0) + 1}`],
          };
        })
      )
    );
  }

  function removeOption(fieldId: string, optionIndex: number) {
    onChange(
      updateFields(schema, (fields) =>
        fields.map((field) => {
          if (field.id !== fieldId || field.type !== 'multiple_choice') {
            return field;
          }
          return {
            ...field,
            options: removeOptionAtIndex(field.options ?? [], optionIndex),
          };
        })
      )
    );
  }

  return (
    <View>
      <Text variant="muted" className="pb-3">
        Ask for details each attendee should provide when registering.
      </Text>

      {schema.fields.length === 0 ? (
        <View className="border-y border-border py-5">
          <Text className="font-bold">No attendee questions</Text>
          <Text variant="muted" className="pt-1">
            Attendees will register without answering anything extra.
          </Text>
        </View>
      ) : null}

      {schema.fields.map((field, fieldIndex) => {
        const isExpanded = expandedFieldId === field.id;
        const fieldError = registrationFormFieldError(field);
        const summary =
          fieldError ??
          `${FIELD_TYPE_LABELS[field.type]} · ${field.required ? 'Required' : 'Optional'}`;

        return (
          <View className="border-b border-border" key={field.id}>
            <Pressable
              accessibilityLabel={`Question ${fieldIndex + 1}, ${
                field.label.trim() || 'Untitled question'
              }, ${summary}`}
              accessibilityRole="button"
              accessibilityState={{ expanded: isExpanded }}
              className="min-h-16 flex-row items-center gap-4 px-1 py-3 active:opacity-60"
              onPress={() =>
                setExpandedFieldId((current) => (current === field.id ? null : field.id))
              }
            >
              <View className="min-w-0 flex-1 gap-0.5">
                <Text className="font-bold" numberOfLines={2}>
                  {field.label.trim() || 'Untitled question'}
                </Text>
                <Text
                  className={fieldError ? 'text-sm text-destructive' : undefined}
                  numberOfLines={1}
                  variant={fieldError ? undefined : 'muted'}
                >
                  {summary}
                </Text>
              </View>
              <Icon
                as={ChevronDownIcon}
                className={`size-5 text-muted-foreground ${isExpanded ? 'rotate-180' : ''}`}
              />
            </Pressable>

            {isExpanded ? (
              <View className="gap-5 px-1 pb-5">
                <View className="gap-1.5">
                  <Label>Question</Label>
                  <Input
                    aria-invalid={!field.label.trim()}
                    className="rounded-none border-x-0 border-t-0 bg-transparent px-0 text-foreground placeholder:text-muted-foreground"
                    onChangeText={(label) => patchField(field.id, { label })}
                    placeholder="What is your expected pace?"
                    value={field.label}
                  />
                  {!field.label.trim() ? (
                    <Text className="text-sm text-destructive" selectable>
                      Add a question.
                    </Text>
                  ) : null}
                </View>

                <View className="min-h-11 flex-row items-center justify-between gap-4">
                  <Label>Answer type</Label>
                  <Host matchContents style={{ maxWidth: 180 }}>
                    <Picker
                      appearance="menu"
                      onValueChange={(type) => setFieldType(field.id, type)}
                      selectedValue={field.type}
                      testID={`question-${field.id}-type`}
                    >
                      {(Object.keys(FIELD_TYPE_LABELS) as RegistrationFormFieldType[]).map(
                        (type) => (
                          <Picker.Item key={type} label={FIELD_TYPE_LABELS[type]} value={type} />
                        )
                      )}
                    </Picker>
                  </Host>
                </View>

                {field.type === 'multiple_choice' ? (
                  <View className="gap-2">
                    <Label>Answer choices</Label>
                    {(field.options ?? []).map((option, optionIndex) => (
                      <View
                        key={`${field.id}-${optionIndex}`}
                        className="flex-row items-center gap-2"
                      >
                        <Input
                          aria-invalid={!option.trim()}
                          className="flex-1 rounded-none border-x-0 border-t-0 bg-transparent px-0 text-foreground placeholder:text-muted-foreground"
                          onChangeText={(value) => patchOption(field.id, optionIndex, value)}
                          placeholder={`Choice ${optionIndex + 1}`}
                          value={option}
                        />
                        <Button
                          accessibilityLabel={`Remove choice ${optionIndex + 1}`}
                          onPress={() => removeOption(field.id, optionIndex)}
                          size="icon"
                          variant="ghost"
                        >
                          <Icon as={Trash2Icon} className="size-4 text-foreground" />
                        </Button>
                      </View>
                    ))}
                    {fieldError === 'Add at least 2 choices' ? (
                      <Text className="text-sm text-destructive" selectable>
                        Add at least 2 different choices.
                      </Text>
                    ) : null}
                    <Button
                      className="self-start"
                      onPress={() => addOption(field.id)}
                      size="sm"
                      variant="secondary"
                    >
                      <Icon as={PlusIcon} className="size-4 text-secondary-foreground" />
                      <Text>Add choice</Text>
                    </Button>
                  </View>
                ) : null}

                <View className="gap-1 border-t border-border pt-4">
                  <Host matchContents style={{ width: '100%' }}>
                    <Switch
                      label="Required"
                      onValueChange={(required) => patchField(field.id, { required })}
                      testID={`question-${field.id}-required`}
                      value={field.required}
                    />
                  </Host>
                  <Text variant="muted">
                    {field.required
                      ? 'Attendees must answer before registering.'
                      : 'Attendees can leave this unanswered.'}
                  </Text>
                </View>

                <View className="gap-2 border-t border-border pt-4">
                  <View className="flex-row gap-2">
                    <View className="flex-1">
                      <Button
                        className="w-full"
                        disabled={fieldIndex === 0}
                        onPress={() => moveField(field.id, -1)}
                        variant="secondary"
                      >
                        <Icon as={ArrowUpIcon} className="size-4 text-secondary-foreground" />
                        <Text>Move up</Text>
                      </Button>
                    </View>
                    <View className="flex-1">
                      <Button
                        className="w-full"
                        disabled={fieldIndex === schema.fields.length - 1}
                        onPress={() => moveField(field.id, 1)}
                        variant="secondary"
                      >
                        <Icon as={ArrowDownIcon} className="size-4 text-secondary-foreground" />
                        <Text>Move down</Text>
                      </Button>
                    </View>
                  </View>
                  <Button onPress={() => removeField(field.id)} variant="destructive">
                    <Icon as={Trash2Icon} className="size-4 text-white" />
                    <Text>Delete question</Text>
                  </Button>
                </View>
              </View>
            ) : null}
          </View>
        );
      })}

      {schema.fields.length >= MAX_REGISTRATION_FORM_FIELDS ? (
        <Text variant="muted" className="pt-4">
          The attendee form can have up to {MAX_REGISTRATION_FORM_FIELDS} questions.
        </Text>
      ) : null}
      <Button
        className="mt-4"
        disabled={schema.fields.length >= MAX_REGISTRATION_FORM_FIELDS}
        onPress={() => addField('short_text')}
      >
        <Icon as={PlusIcon} className="size-4 text-primary-foreground" />
        <Text>Add question</Text>
      </Button>
    </View>
  );
}

function RegistrationResponseControl({
  isSubmitting,
  onBlur,
  onChange,
  question,
  value,
}: Readonly<{
  isSubmitting?: boolean;
  onBlur: () => void;
  onChange: (value: string) => void;
  question: RegistrationFormField;
  value: string;
}>) {
  if (question.type === 'multiple_choice') {
    return (
      <View className="flex-row flex-wrap gap-2">
        {(question.options ?? []).map((option) => (
          <Button
            key={option}
            disabled={isSubmitting}
            onPress={() => onChange(option)}
            size="sm"
            variant={value === option ? 'default' : 'outline'}
          >
            <Text>{option}</Text>
          </Button>
        ))}
      </View>
    );
  }

  return (
    <Input
      editable={!isSubmitting}
      multiline={question.type === 'long_text'}
      numberOfLines={question.type === 'long_text' ? 4 : 1}
      onBlur={onBlur}
      onChangeText={onChange}
      placeholder={question.type === 'long_text' ? 'Add details' : 'Your answer'}
      value={value}
      className={
        question.type === 'long_text'
          ? 'h-24 border-input bg-card py-2 text-foreground placeholder:text-muted-foreground'
          : 'border-input bg-card text-foreground placeholder:text-muted-foreground'
      }
    />
  );
}

function RegistrationResponseField({
  control,
  errorMessage,
  isSubmitting,
  question,
}: Readonly<{
  control: Control<RegistrationFormResponses>;
  errorMessage?: unknown;
  isSubmitting?: boolean;
  question: RegistrationFormField;
}>) {
  return (
    <View className="gap-2">
      <Label>
        {question.label}
        {question.required ? ' *' : ''}
      </Label>
      <Controller
        control={control}
        name={question.id}
        render={({ field }) => (
          <RegistrationResponseControl
            isSubmitting={isSubmitting}
            onBlur={field.onBlur}
            onChange={field.onChange}
            question={question}
            value={field.value ?? ''}
          />
        )}
      />
      {errorMessage ? (
        <Text className="text-sm text-destructive" selectable>
          {String(errorMessage)}
        </Text>
      ) : null}
    </View>
  );
}

export function EventRegistrationResponseForm({
  defaultResponses,
  error,
  isSubmitting,
  onSubmit,
  schema,
  submitLabel = 'Save answers',
}: Readonly<{
  defaultResponses?: RegistrationFormResponses;
  error?: string | null;
  isSubmitting?: boolean;
  onSubmit: (responses: RegistrationFormResponses) => void | Promise<void>;
  schema: RegistrationFormSchema;
  submitLabel?: string;
}>) {
  const defaultValues = React.useMemo(
    () =>
      Object.fromEntries(
        schema.fields.map((field) => [field.id, defaultResponses?.[field.id] ?? ''])
      ),
    [defaultResponses, schema.fields]
  );
  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
  } = useForm<RegistrationFormResponses>({
    defaultValues,
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  function submit(values: RegistrationFormResponses) {
    clearErrors();
    const validation = validateRegistrationFormResponses(schema, values);
    if (!validation.valid) {
      for (const [fieldId, message] of Object.entries(validation.errors)) {
        setError(fieldId, { message, type: 'validate' });
      }
      return;
    }

    void onSubmit(validation.responses);
  }

  return (
    <View className="gap-4">
      <View className="gap-1">
        <Text variant="large">Registration questions</Text>
        <Text variant="muted">Answer these before confirming your registration.</Text>
      </View>

      {schema.fields.map((field) => (
        <RegistrationResponseField
          key={field.id}
          control={control}
          errorMessage={errors[field.id]?.message}
          isSubmitting={isSubmitting}
          question={field}
        />
      ))}

      {error ? (
        <Text className="text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}

      <Button disabled={isSubmitting} onPress={handleSubmit(submit)}>
        <Text>{isSubmitting ? 'Saving...' : submitLabel}</Text>
      </Button>
    </View>
  );
}
